/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prefer-destructuring */
import React, {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  filterRgHash,
  getUpdatedRgHash,
  getRoleGroupPrn,
} from '@mummssoftware/utils/frequencies/frequency-utils';
import { isAgencyEnabled } from '@mummssoftware/utils/utils';
import cloneDeep from 'lodash-es/cloneDeep';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import {
  VFGlobalContext,
  flattenOrderEntries,
} from '@mummssoftware/global-context/VFGlobalContext';
import { CarePlanFrequencyContext } from '../../context';
import { applyOrderModeToRows } from '../../utils/orderRows';
import {
  SET_NEW_ROW,
  SET_AG_ROWS,
  SET_EDITING_ROLEGROUP,
  RESET_EDITING_ROLEGROUP,
  CLEAR_NEW_ROW,
  SET_EDIT_ROW_OPEN,
  SET_AG_ROW_EDTING_DATA,
  SET_API_ERROR,
} from './constants';

type CPFContextProviderProps = {
  children: ReactNode;
};

type CPFContext = {
  newRows: any;
  agRowData: any;
  tableRgHash: mumms.HashedRoleGroup | null;
  isCPFAgency: boolean | null | '';
  isPRNOrderAgency: boolean | null | '';
  nonOrderEditing: boolean | null | '';
  editingRoleGroupId: string | null;
  editRowOpen: boolean;
  agEditingData: any;
  errorAPIInfo?: mumms.ErrorInfo | null;
  dispatch?: any;
  clearAPIerror: () => any;
};

type CPFContextState = {
  newRows: any;
  agRowData: any;
  editingRoleGroupId: string | null;
  editRowOpen: boolean;
  dispatch?: any;
  agEditingData: any;
  errorAPIInfo?: mumms.ErrorInfo | null;
};

const initialState = {
  newRows: null,
  agRowData: [],
  editingRoleGroupId: null,
  editRowOpen: false,
  agEditingData: null,
  errorAPIInfo: null,
};

const initCPFContext = {
  newRows: null,
  agRowData: [],
  tableRgHash: null,
  editingRoleGroupId: null,
  editRowOpen: false,
  agEditingData: null,
  errorAPIInfo: null,
};

export const CPFContextReducer = (
  state: CPFContextState,
  action: { type: string; payload: any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_NEW_ROW:
      if (newState.newRows) {
        newState.newRows = newState.newRows.concat(action.payload);
      } else {
        newState.newRows = [action.payload];
      }
      break;
    case CLEAR_NEW_ROW:
      newState.newRows = [];
      break;
    case SET_AG_ROWS:
      newState.agRowData = action.payload;
      break;
    case SET_AG_ROW_EDTING_DATA:
      newState.agEditingData = action.payload;
      break;
    case SET_EDITING_ROLEGROUP:
      newState.editingRoleGroupId = state.editingRoleGroupId || action.payload;
      break;
    case RESET_EDITING_ROLEGROUP:
      newState.editingRoleGroupId = null;
      break;
    case SET_EDIT_ROW_OPEN:
      newState.editRowOpen = action.payload;
      break;
    case SET_API_ERROR:
      newState.errorAPIInfo = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const CPFContext = createContext<CPFContext>(
  initCPFContext as CPFContext,
);
export const CPFContextProvider = ({ children }: CPFContextProviderProps) => {
  const [
    {
      newRows,
      agRowData,
      editingRoleGroupId,
      editRowOpen,
      agEditingData,
      errorAPIInfo,
    },
    dispatch,
  ] = useReducer(CPFContextReducer, initialState);
  const {
    rgHash,
    carePlanFrequencies,
    patient,
    currentUser,
    idgClonedRgHash,
    idgData,
    refreshUI,
  } = useContext(CarePlanFrequencyContext);
  const { carePlanFrequenciesGlobal, generalStandingOrderGlobal } = useContext(
    GlobalContext,
  );
  const { VForderData, VFordersMode } = useContext(VFGlobalContext);

  const cpfOrderWidgetBins: string = window.env?.cpfOrderWidgetBins as string;
  const isCPFAgency = isAgencyEnabled(cpfOrderWidgetBins, currentUser);
  const prnOrderWidgetBins: string = window.env?.prnOrderWidgetBins as string;
  const isPRNOrderAgency = isAgencyEnabled(prnOrderWidgetBins, currentUser);
  const nonOrderEditing = !VFordersMode && isPRNOrderAgency;

  const rowVersionRef = useRef(0);
  const [tableRgHash, setTableRgHash] = useState<mumms.HashedRoleGroup | null>(
    null,
  );

  const buildTableRgHash = async () => {
    const sourceHash = idgClonedRgHash || rgHash;
    if (!sourceHash) return null;
    const hashClone = cloneDeep(sourceHash);
    const updatedRgHash = await getUpdatedRgHash(
      idgData?.frequencies || carePlanFrequenciesGlobal,
      hashClone,
    );
    return generalStandingOrderGlobal
      ? getRoleGroupPrn(generalStandingOrderGlobal, updatedRgHash)
      : updatedRgHash;
  };

  const updateTable = (hash: mumms.HashedRoleGroup) => {
    const filteredRgHash = filterRgHash(hash, newRows, VForderData);
    const orderModeRows = applyOrderModeToRows(
      filteredRgHash,
      hash,
      VForderData,
      VFordersMode,
    );
    const hasOrderEntries =
      VFordersMode && flattenOrderEntries(VForderData).length > 0;
    if (!orderModeRows?.length && hasOrderEntries) {
      return;
    }
    rowVersionRef.current += 1;
    const rowVersion = rowVersionRef.current;
    const updatedRows =
      orderModeRows && orderModeRows.length
        ? orderModeRows.map((data: mumms.HashedRoleGroup) => ({
            ...data,
            count: rowVersion,
            patientNumber: patient?.patientNumber,
            editing:
              VFordersMode &&
              (hasOrderEntries
                ? !!(data as any).orderEntryId
                : !!(data as any).isOrderAddTarget),
          }))
        : [];
    dispatch({
      type: SET_AG_ROWS,
      payload: updatedRows,
    });
  };

  // Rebuild tableRgHash only when source care-plan data changes.
  // Never on VForderData / dialog field edits — getUpdatedRgHash clears
  // frequencies in-place and must not run during order dialog editing.
  useEffect(() => {
    if (!rgHash) return undefined;
    let cancelled = false;
    buildTableRgHash().then((hash) => {
      if (!cancelled && hash) {
        setTableRgHash(hash);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    rgHash,
    carePlanFrequencies,
    carePlanFrequenciesGlobal,
    generalStandingOrderGlobal,
  ]);

  // Rebuild grid rows when source hash / order staging / new rows change.
  // Do NOT depend on editingRoleGroupId or editRowOpen — those only gate cell
  // UI via context; including them caused an extra SET_AG_ROWS (+ AgGrid paint)
  // on every inline lock/unlock (confirmed in save-flicker profiles).
  useEffect(() => {
    if (!tableRgHash) return;
    updateTable(tableRgHash);
  }, [tableRgHash, newRows, VFordersMode, VForderData]);

  useEffect(() => {
    if (refreshUI) {
      dispatch({ type: CLEAR_NEW_ROW, payload: null });
    }
  }, [refreshUI]);

  const clearAPIerror = () => {
    dispatch({
      type: SET_API_ERROR,
      payload: null,
    });
  };
  return (
    <CPFContext.Provider
      value={{
        newRows,
        agRowData,
        tableRgHash,
        isCPFAgency,
        isPRNOrderAgency,
        nonOrderEditing,
        editingRoleGroupId,
        editRowOpen,
        agEditingData,
        errorAPIInfo,
        clearAPIerror,
        dispatch,
      }}
    >
      {children}
    </CPFContext.Provider>
  );
};

export default CPFContextProvider;
